"use client";

import type {
	CoreFieldType,
	FieldBooleanChrome,
	FieldPresentation,
} from "../../lib/field-types/contracts";
import type { ReactNode } from "react";
import { AssetField, type AssetRef } from "./asset-field";
import { BooleanField } from "./boolean-field";
import { BooleanGroupField, type BooleanGroupOption } from "./boolean-group-field";
import { CustomField } from "./custom-field";
import {
	DiscreteScaleField,
	type DiscreteScaleOption,
} from "./discrete-scale-field";
import type { FieldLayoutHint } from "./field-shell";
import { IntRangeField, type IntRangeMark } from "./int-range-field";
import { MultiSelectField } from "./multi-select-field";
import { NumberField, type NumberFieldChromeProps } from "./number-field";
import { OtpField } from "./otp-field";
import { PasswordField } from "./password-field";
import { RichtextField } from "./richtext-field";
import {
	SingleSelectField,
	type SingleSelectOption,
} from "./single-select-field";
import { TextareaField } from "./textarea-field";
import { TextField } from "./text-field";

export interface FieldRendererBaseProps {
	id: string;
	label: string;
	description?: string;
	error?: ReactNode;
	reserveErrorSpace?: boolean;
	name?: string;
	onBlur?: () => void;
	layoutHint?: FieldLayoutHint;
	className?: string;
	presentation?: FieldPresentation;
	mixed?: boolean;
	mixedTooltip?: ReactNode;
	mixedPlaceholder?: string;
	disabled?: boolean;
}

export type FieldRendererProps = FieldRendererBaseProps &
	(
		| {
				fieldType: "text";
				value: string;
				onChange: (value: string) => void;
		  }
		| {
				fieldType: "textarea";
				value: string;
				onChange: (value: string) => void;
				maxChars?: number;
				minRows?: number;
		  }
		| {
				fieldType: "richtext";
				editorSlot?: ReactNode;
				toolbarSlot?: ReactNode;
		  }
		| {
				fieldType: "number";
				value: number;
				onChange: (
					value: number,
					source: "spinner" | "slider" | "input",
				) => void;
				chrome?: NumberFieldChromeProps;
		  }
		| {
				fieldType: "boolean";
				value: boolean | null;
				onChange: (value: boolean) => void;
				trueLabel?: string;
				falseLabel?: string;
				chrome?: FieldBooleanChrome;
				align?: "start" | "center";
				disabledReason?: string;
				testId?: string;
		  }
		| {
				fieldType: "boolean_group";
				value: Record<string, boolean>;
				options: BooleanGroupOption[];
				onChange: (value: Record<string, boolean>) => void;
		  }
		| {
				fieldType: "single_select";
				value: string | null;
				options: SingleSelectOption[];
				onChange: (value: string) => void;
				displayValue?: string | null;
				placeholder?: string;
				tileVariant?: "compact" | "large" | "settings";
				hideLabel?: boolean;
				segmentedOptionDisplay?: "icon" | "text";
				layoutId?: string;
				size?: "default" | "compact";
				layoutAnimationsEnabled?: boolean;
				highlightWhenRightActive?: boolean;
		  }
		| {
				fieldType: "multi_select";
				value: string[];
				options: SingleSelectOption[];
				onChange: (value: string[]) => void;
				presentation?: "tiles" | "check_menu";
		  }
		| {
				fieldType: "int_range";
				min: number;
				max: number;
				step: number;
				currentMin?: number | null;
				currentMax?: number | null;
				marks?: IntRangeMark[];
				formatHint?: string;
				settingType?: string;
				onSelect?: (settingType: string, value: string) => void;
				onChange?: (range: { min: number; max: number }) => void;
		  }
		| {
				fieldType: "number_range";
				min: number;
				max: number;
				step: number;
				currentMin?: number | null;
				currentMax?: number | null;
				onChange?: (range: { min: number; max: number }) => void;
		  }
		| {
				fieldType: "discrete_scale";
				value: string;
				options: DiscreteScaleOption[];
				onChange: (value: string) => void;
				lowAxisLabel: string;
				highAxisLabel: string;
				title?: string;
				valueLabel?: string;
				helpText?: ReactNode;
				helpAriaLabel?: string;
				maxGradientFrom?: string;
				maxGradientTo?: string;
				applyMaxGradient?: boolean;
				omitShell?: boolean;
				testId?: string;
		  }
		| {
				fieldType: "asset";
				value: AssetRef[];
				onChange?: (value: AssetRef[]) => void;
				actionsSlot?: ReactNode;
		  }
		| {
				fieldType: "custom";
				subtype?: string;
				children?: ReactNode;
		  }
		| {
				fieldType: "password";
				value: string;
				onChange: (value: string) => void;
				showPasswordLabel: string;
				hidePasswordLabel: string;
				hideLabel?: boolean;
				autoComplete?: string;
		  }
		| {
				fieldType: "otp";
				value: string;
				onChange: (value: string) => void;
				length?: number;
				hideLabel?: boolean;
				autoComplete?: string;
		  }
	);

function resolveBooleanPresentation(
	presentation: FieldPresentation | undefined,
): "segmented" | "switch" | "tiles" | "checkbox" {
	if (
		presentation === "switch" ||
		presentation === "tiles" ||
		presentation === "checkbox"
	) {
		return presentation;
	}
	return "segmented";
}

function resolveSingleSelectPresentation(
	presentation: FieldPresentation | undefined,
): "tiles" | "dropdown" | "radio" | "segmented" | "check_menu" {
	if (
		presentation === "dropdown" ||
		presentation === "radio" ||
		presentation === "segmented" ||
		presentation === "check_menu"
	) {
		return presentation;
	}
	return "tiles";
}

/**
 * Dispatches every {@link CoreFieldType} to its atom.
 */
export function FieldRenderer(props: FieldRendererProps) {
	const {
		id,
		label,
		description,
		error,
		reserveErrorSpace,
		name,
		onBlur,
		layoutHint,
		className,
		mixed,
		mixedTooltip,
		mixedPlaceholder,
		disabled,
	} = props;

	switch (props.fieldType) {
		case "text":
			return (
				<TextField
					id={id}
					label={label}
					description={description}
					error={error}
					reserveErrorSpace={reserveErrorSpace}
					name={name}
					onBlur={onBlur}
					layoutHint={layoutHint}
					className={className}
					value={props.value}
					onChange={props.onChange}
					mixed={mixed}
					mixedTooltip={mixedTooltip}
					mixedPlaceholder={mixedPlaceholder}
					disabled={disabled}
				/>
			);
		case "textarea":
			return (
				<TextareaField
					id={id}
					label={label}
					description={description}
					error={error}
					reserveErrorSpace={reserveErrorSpace}
					name={name}
					onBlur={onBlur}
					layoutHint={layoutHint}
					className={className}
					value={props.value}
					onChange={props.onChange}
					maxChars={props.maxChars}
					minRows={props.minRows}
					mixed={mixed}
					mixedTooltip={mixedTooltip}
					mixedPlaceholder={mixedPlaceholder}
					disabled={disabled}
				/>
			);
		case "richtext":
			return (
				<RichtextField
					id={id}
					label={label}
					description={description}
					error={error}
					reserveErrorSpace={reserveErrorSpace}
					name={name}
					onBlur={onBlur}
					disabled={disabled}
					layoutHint={layoutHint}
					className={className}
					editorSlot={props.editorSlot}
					toolbarSlot={props.toolbarSlot}
				/>
			);
		case "number":
			return (
				<NumberField
					id={id}
					label={label}
					description={description}
					error={error}
					reserveErrorSpace={reserveErrorSpace}
					name={name}
					onBlur={onBlur}
					layoutHint={layoutHint}
					className={className}
					value={props.value}
					onChange={props.onChange}
					disabled={disabled}
					mixed={mixed}
					mixedTooltip={mixedTooltip}
					{...props.chrome}
				/>
			);
		case "boolean":
			return (
				<BooleanField
					id={id}
					label={label}
					description={description}
					error={error}
					reserveErrorSpace={reserveErrorSpace}
					name={name}
					onBlur={onBlur}
					layoutHint={layoutHint}
					className={className}
					value={props.value}
					onChange={props.onChange}
					presentation={resolveBooleanPresentation(props.presentation)}
					chrome={props.chrome}
					align={props.align}
					mixed={mixed}
					mixedTooltip={mixedTooltip}
					mixedPlaceholder={mixedPlaceholder}
					trueLabel={props.trueLabel}
					falseLabel={props.falseLabel}
					disabled={disabled}
					disabledReason={props.disabledReason}
					testId={props.testId}
				/>
			);
		case "boolean_group":
			return (
				<BooleanGroupField
					id={id}
					label={label}
					description={description}
					error={error}
					reserveErrorSpace={reserveErrorSpace}
					name={name}
					onBlur={onBlur}
					layoutHint={layoutHint}
					className={className}
					value={props.value}
					options={props.options}
					onChange={props.onChange}
					disabled={disabled}
					mixed={mixed}
					mixedTooltip={mixedTooltip}
					mixedPlaceholder={mixedPlaceholder}
				/>
			);
		case "single_select":
			return (
				<SingleSelectField
					id={id}
					label={label}
					description={description}
					error={error}
					reserveErrorSpace={reserveErrorSpace}
					name={name}
					onBlur={onBlur}
					layoutHint={layoutHint}
					className={className}
					value={props.value}
					options={props.options}
					onChange={props.onChange}
					presentation={resolveSingleSelectPresentation(
						props.presentation,
					)}
					mixed={mixed}
					mixedTooltip={mixedTooltip}
					mixedPlaceholder={mixedPlaceholder}
					displayValue={props.displayValue}
					placeholder={props.placeholder}
					tileVariant={props.tileVariant}
					hideLabel={props.hideLabel}
					segmentedOptionDisplay={props.segmentedOptionDisplay}
					layoutId={props.layoutId}
					size={props.size}
					layoutAnimationsEnabled={props.layoutAnimationsEnabled}
					highlightWhenRightActive={props.highlightWhenRightActive}
					disabled={disabled}
				/>
			);
		case "multi_select":
			return (
				<MultiSelectField
					id={id}
					label={label}
					description={description}
					error={error}
					reserveErrorSpace={reserveErrorSpace}
					name={name}
					onBlur={onBlur}
					layoutHint={layoutHint}
					className={className}
					value={props.value}
					options={props.options}
					onChange={props.onChange}
					presentation={props.presentation}
					disabled={disabled}
					mixed={mixed}
					mixedTooltip={mixedTooltip}
					mixedPlaceholder={mixedPlaceholder}
				/>
			);
		case "int_range":
		case "number_range":
			return (
				<IntRangeField
					id={id}
					prompt={label}
					description={description}
					error={error}
					reserveErrorSpace={reserveErrorSpace}
					name={name}
					onBlur={onBlur}
					disabled={disabled}
					layoutHint={layoutHint}
					className={className}
					min={props.min}
					max={props.max}
					step={props.step}
					currentMin={props.currentMin}
					currentMax={props.currentMax}
					marks={"marks" in props ? props.marks : undefined}
					formatHint={
						"formatHint" in props ? props.formatHint : undefined
					}
					settingType={
						"settingType" in props ? props.settingType : undefined
					}
					onSelect={"onSelect" in props ? props.onSelect : undefined}
					onChange={props.onChange}
					mixed={mixed}
					mixedTooltip={mixedTooltip}
					mixedPlaceholder={mixedPlaceholder}
				/>
			);
		case "discrete_scale":
			return (
				<DiscreteScaleField
					id={id}
					label={label}
					description={description}
					error={error}
					reserveErrorSpace={reserveErrorSpace}
					name={name}
					onBlur={onBlur}
					disabled={disabled}
					layoutHint={layoutHint}
					className={className}
					value={props.value}
					options={props.options}
					onChange={props.onChange}
					lowAxisLabel={props.lowAxisLabel}
					highAxisLabel={props.highAxisLabel}
					title={props.title}
					valueLabel={props.valueLabel}
					helpText={props.helpText}
					helpAriaLabel={props.helpAriaLabel}
					maxGradientFrom={props.maxGradientFrom}
					maxGradientTo={props.maxGradientTo}
					applyMaxGradient={props.applyMaxGradient}
					omitShell={props.omitShell}
					testId={props.testId}
				/>
			);
		case "asset":
			return (
				<AssetField
					id={id}
					label={label}
					description={description}
					error={error}
					reserveErrorSpace={reserveErrorSpace}
					name={name}
					onBlur={onBlur}
					disabled={disabled}
					layoutHint={layoutHint}
					className={className}
					value={props.value}
					onChange={props.onChange}
					actionsSlot={props.actionsSlot}
				/>
			);
		case "custom":
			return (
				<CustomField
					id={id}
					label={label}
					description={description}
					error={error}
					reserveErrorSpace={reserveErrorSpace}
					name={name}
					onBlur={onBlur}
					disabled={disabled}
					layoutHint={layoutHint}
					className={className}
					subtype={props.subtype}
				>
					{props.children}
				</CustomField>
			);
		case "password":
			return (
				<PasswordField
					id={id}
					label={label}
					description={description}
					error={error}
					reserveErrorSpace={reserveErrorSpace}
					name={name}
					onBlur={onBlur}
					disabled={disabled}
					layoutHint={layoutHint}
					className={className}
					value={props.value}
					onChange={props.onChange}
					showPasswordLabel={props.showPasswordLabel}
					hidePasswordLabel={props.hidePasswordLabel}
					hideLabel={props.hideLabel}
					autoComplete={props.autoComplete}
				/>
			);
		case "otp":
			return (
				<OtpField
					id={id}
					label={label}
					description={description}
					error={error}
					reserveErrorSpace={reserveErrorSpace}
					name={name}
					onBlur={onBlur}
					disabled={disabled}
					layoutHint={layoutHint}
					className={className}
					value={props.value}
					onChange={props.onChange}
					length={props.length}
					hideLabel={props.hideLabel}
					autoComplete={props.autoComplete}
				/>
			);
		default: {
			const _exhaustive: never = props;
			return _exhaustive;
		}
	}
}

export function isDispatchableFieldType(
	fieldType: string,
): fieldType is CoreFieldType {
	return (
		fieldType === "text" ||
		fieldType === "textarea" ||
		fieldType === "richtext" ||
		fieldType === "number" ||
		fieldType === "boolean" ||
		fieldType === "boolean_group" ||
		fieldType === "single_select" ||
		fieldType === "multi_select" ||
		fieldType === "int_range" ||
		fieldType === "number_range" ||
		fieldType === "discrete_scale" ||
		fieldType === "asset" ||
		fieldType === "custom" ||
		fieldType === "password" ||
		fieldType === "otp"
	);
}
